import { formatTipPayoutDisplayLabel, extractManagerRecipientName } from './tipPayoutPaidStatus';

export const TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER = 'pending_paid_to_manager';

const UNPAID_TIP_PAYOUT_STATUSES = new Set([
    'failed',
    'pending',
    'partial',
    'not_eligible',
    'reversed',
    TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER,
]);

export function isManagerRoutedPayReason(manualPayReason) {
    if (manualPayReason === null || manualPayReason === undefined) {
        return false;
    }
    const trimmed = String(manualPayReason).trim();
    if (!trimmed) return false;
    return /^Paid to Manager'?s Account/i.test(trimmed)
        || /^Paid by Manager/i.test(trimmed)
        || /^Pending payment to employee \(paid to manager/i.test(trimmed)
        || /Paid to Manager'?s Account/i.test(trimmed);
}

/** Pooled deferred bookkeeping is not Mark as Paid (Manual). */
export function isPooledDeferredPayReason(manualPayReason) {
    if (manualPayReason === null || manualPayReason === undefined) {
        return false;
    }
    const trimmed = String(manualPayReason).trim();
    if (!trimmed) return false;
    return /^pooled_deferred:/i.test(trimmed)
        || /^Paid manually:\s*pooled_deferred:/i.test(trimmed);
}

export function isPooledDeferredAwaitingPayReason(manualPayReason) {
    if (!isPooledDeferredPayReason(manualPayReason)) return false;
    const trimmed = String(manualPayReason).trim()
        .replace(/^Paid manually:\s*/i, '');
    return !/^pooled_deferred:\s*settled/i.test(trimmed);
}

function extractManagerPaidAmount(manualPayReason, fallbackAmount) {
    if (manualPayReason !== null && manualPayReason !== undefined) {
        const fromReason = String(manualPayReason).match(/—\s*\$([0-9]+(?:\.[0-9]+)?)/);
        if (fromReason) {
            const parsed = Number(fromReason[1]);
            if (Number.isFinite(parsed)) return parsed;
        }
    }
    if (fallbackAmount === null || fallbackAmount === undefined || fallbackAmount === '') {
        return null;
    }
    const fallback = Number(fallbackAmount);
    return Number.isFinite(fallback) ? fallback : null;
}

function formatCurrencyAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return null;
    return `$${parsed.toFixed(2)}`;
}

function formatPendingPaidToManagerLabel(manualPayReason, amountPaid) {
    const recipient = extractManagerRecipientName(manualPayReason);
    const base = recipient
        ? `Pending payment to employee (paid to manager) (${recipient})`
        : 'Pending payment to employee (paid to manager)';
    const amount = extractManagerPaidAmount(manualPayReason, amountPaid);
    const formatted = formatCurrencyAmount(amount);
    return formatted ? `${base} — ${formatted}` : base;
}

/**
 * Resolve display status without letting stale local/route "paid" override
 * authoritative unpaid API states (failed / pending / partial / not_eligible /
 * pending_paid_to_manager).
 */
export function resolveEffectiveTipPayoutStatus({
    apiStatus,
    routeTransferStatus = null,
    isLocallyPaid = false,
    hasNoUnpaidTransfers = false,
    tipsOwedAmount = null,
    manualPayReason = null,
    employeeSettledAmount = null,
    tipsEarnedAmount = null,
} = {}) {
    let api = String(apiStatus || 'none').toLowerCase();
    const route = routeTransferStatus
        ? String(routeTransferStatus).toLowerCase()
        : null;
    const owed = Number(tipsOwedAmount);
    const hasOwedAmountField = tipsOwedAmount !== null && tipsOwedAmount !== undefined && tipsOwedAmount !== '';
    const hasOwed = hasOwedAmountField && Number.isFinite(owed) && owed > 0.01;
    const explicitlyZeroOwed = hasOwedAmountField && Number.isFinite(owed) && owed <= 0.01;
    const earned = Number(tipsEarnedAmount);
    const employeeSettled = Number(employeeSettledAmount);
    const employeeHistoryCoversShift = Number.isFinite(earned) && earned > 0.01
        && Number.isFinite(employeeSettled)
        && employeeSettled + 0.01 >= earned;

    // Legacy: manager-routed rows stored as paid + manager ManualPayReason.
    if (api === 'paid' && isManagerRoutedPayReason(manualPayReason) && !employeeHistoryCoversShift) {
        api = TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER;
    }

    // Pooled deferred awaiting settlement is still unpaid — never "Paid manually".
    if ((api === 'paid' || api === 'none')
        && isPooledDeferredAwaitingPayReason(manualPayReason)
        && !employeeHistoryCoversShift) {
        return 'pending';
    }

    // Historical employee payout covering the shift stays paid even if live
    // Stripe / Tips Off caused the API to recast as paid-to-manager.
    if (api === TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER && employeeHistoryCoversShift) {
        return 'paid';
    }

    // Obligation fully covered (TipsOwedAmount === 0): leftover failed/partial
    // transfer rows must not keep the shift in an unpaid status — except manager-
    // routed pending, which stays unpaid until Mark as Paid (Manual).
    if (explicitlyZeroOwed
        && (api === 'failed' || api === 'partial' || api === 'reversed')) {
        return 'paid';
    }

    // API unpaid statuses always win — including over local/route "paid".
    // Tip-history mixed outcome (some paid to employee, some still held) upgrades
    // a coarse API "pending" to "partial" so Work Period Details can show both amounts.
    if (UNPAID_TIP_PAYOUT_STATUSES.has(api)) {
        if (api === 'pending' && route === 'partial') {
            return 'partial';
        }
        return api;
    }

    // Backend still reports money owed: never show fully paid.
    if (hasOwed && api === 'paid') {
        return 'partial';
    }
    if (hasOwed && api === 'none') {
        return route && UNPAID_TIP_PAYOUT_STATUSES.has(route) ? route : 'pending';
    }

    if (hasNoUnpaidTransfers || api === 'paid') {
        return 'paid';
    }

    // Route history can refine an inconclusive API status, but unpaid route
    // must not be upgraded to paid by localStorage.
    if (route && UNPAID_TIP_PAYOUT_STATUSES.has(route)) {
        return route;
    }

    // Never invent "paid" from tip-history alone when the sales API did not
    // confirm paid — history pagination is incomplete and was a false-positive source.
    if (isLocallyPaid && (api === 'none' || !apiStatus)) {
        return 'paid';
    }

    return api;
}

export function formatTipPayoutStatusLabel(status, manualPayReason, amountPaid) {
    const normalized = (status || 'none').toLowerCase();

    if (normalized === TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER) {
        return formatPendingPaidToManagerLabel(manualPayReason, amountPaid);
    }

    // Deferred bookkeeping must never render as Paid manually.
    if (isPooledDeferredAwaitingPayReason(manualPayReason)
        || (normalized === 'pending' && isPooledDeferredPayReason(manualPayReason))) {
        return 'Awaiting work-period pooled settlement';
    }

    if (normalized === 'paid' || normalized === 'partial') {
        // Legacy paid + manager reason → pending label (safety if resolve was skipped).
        // Do not recast when the caller already resolved historical employee payout as paid.
        if (normalized === 'paid' && isManagerRoutedPayReason(manualPayReason)) {
            return formatPendingPaidToManagerLabel(manualPayReason, amountPaid);
        }

        if (normalized === 'paid' && isPooledDeferredPayReason(manualPayReason)) {
            return 'Awaiting work-period pooled settlement';
        }

        const structuredLabel = formatTipPayoutDisplayLabel(manualPayReason);
        if (structuredLabel) {
            return normalized === 'partial' ? `${structuredLabel} (Partial)` : structuredLabel;
        }

        if (manualPayReason !== null && manualPayReason !== undefined) {
            const trimmed = String(manualPayReason).trim();
            if (trimmed) {
                if (isManagerRoutedPayReason(trimmed)) {
                    return formatPendingPaidToManagerLabel(trimmed, amountPaid);
                }
                if (isPooledDeferredPayReason(trimmed)) {
                    return 'Awaiting work-period pooled settlement';
                }
                return trimmed.startsWith('Paid manually')
                    ? trimmed
                    : `Paid manually: ${trimmed}`;
            }
            return normalized === 'partial' ? 'Partial' : 'Paid manually';
        }

        return normalized === 'partial' ? 'Partial' : 'Paid';
    }

    if (normalized === 'failed') return 'Failed';
    if (normalized === 'pending') return 'Pending';
    if (normalized === 'not_eligible') return 'Not eligible';
    if (normalized === 'reversed') return 'Reversed';
    if (normalized === 'none') return 'None';

    return status || 'None';
}

export function resolveManualPayReasonFromRow(row, fallbackReason) {
    if (!row) {
        return fallbackReason || undefined;
    }

    if (row.ManualPayReason !== undefined && row.ManualPayReason !== null) {
        return row.ManualPayReason;
    }

    if (row.manualPayReason !== undefined && row.manualPayReason !== null) {
        return row.manualPayReason;
    }

    return fallbackReason || undefined;
}

export function resolveTipPayoutNoteFromRow(row, fallbackNote) {
    if (!row) {
        return fallbackNote || null;
    }

    const note = row.TipPayoutNote ?? row.tipPayoutNote;
    if (note !== undefined && note !== null && String(note).trim()) {
        return String(note).trim();
    }

    return fallbackNote || null;
}

/**
 * Paid vs remaining unpaid for Work Period Details when tip history shows a mixed
 * employee payout (some TipTransfers completed to the employee, remainder held).
 * Manager-routed amounts must not be included in employeePaidAmount by the caller.
 * When Mark as Paid (Manual) has settled the shift, pass manuallySettled so Pending is 0.
 */
export function buildWorkPeriodPayoutAmountSplit({
    totalOwed = 0,
    employeePaidAmount = 0,
    manuallySettled = false,
} = {}) {
    const round2 = (n) => Math.round(n * 100) / 100;
    const owed = Math.max(0, Number(totalOwed) || 0);
    if (manuallySettled && owed > 0) {
        return {
            paidAmount: round2(owed),
            pendingAmount: 0,
            showSplit: false,
        };
    }
    const paid = Math.max(0, Number(employeePaidAmount) || 0);
    const pending = Math.max(0, round2(owed - paid));
    const showSplit = paid > 0.01 && pending > 0.01;
    return {
        paidAmount: round2(paid),
        pendingAmount: pending,
        showSplit,
    };
}

/**
 * Amount still owed for the green Pay out Tips CTA / Available gate.
 * Source of truth is shift earnings − already paid to the employee.
 * Open TipTransfer rows can sum higher when leftover awaiting_funds rows remain
 * after a partial payout — never inflate the CTA above remaining obligation.
 */
export function getRemainingTipPayoutAmount({
    totalOwed = 0,
    employeePaidAmount = 0,
    pendingTransferAmount = 0,
} = {}) {
    const round2 = (n) => Math.round(n * 100) / 100;
    const owed = Math.max(0, Number(totalOwed) || 0);
    const paid = Math.max(0, Number(employeePaidAmount) || 0);
    const transferPending = Math.max(0, Number(pendingTransferAmount) || 0);
    const remainingOwed = Math.max(0, round2(owed - paid));

    if (paid > 0.01) {
        return remainingOwed;
    }
    if (transferPending > 0.005) {
        return round2(transferPending);
    }
    return owed;
}

/**
 * When completed Stripe payouts nearly cover shift earnings but TipTransfer rows
 * are still pending/awaiting_funds, the open-row sum can exceed remaining owed.
 */
export function describeLeftoverPendingTransfers({
    remainingOwed = 0,
    pendingTransferAmount = 0,
    pendingCount = 0,
} = {}) {
    const remaining = Math.max(0, Number(remainingOwed) || 0);
    const openAmount = Math.max(0, Number(pendingTransferAmount) || 0);
    const count = Math.max(0, Number(pendingCount) || 0);
    if (count <= 0 || remaining <= 0.005) {
        return null;
    }
    if (openAmount > remaining + 0.02) {
        return (
            `${count} open transfer row(s) still show $${openAmount.toFixed(2)} pending or waiting for funds, `
            + `but only $${remaining.toFixed(2)} of this shift's tips is still unpaid. `
            + `Pay out Tips sends at most the unpaid remainder — use Mark as Paid (Manual) to clear leftover rows if they are dust or duplicates.`
        );
    }
    return (
        `${count} transfer(s) totaling $${Math.min(openAmount, remaining).toFixed(2)} still pending or waiting for funds.`
    );
}

/**
 * Build explanatory notes when tip history shows a split outcome
 * (some completed, some failed/pending) relative to shift earnings.
 */
export function buildSplitPayoutStatusNotes({
    status,
    totalOwed = 0,
    completedAmount = 0,
    failedAmount = 0,
    failedCount = 0,
    pendingAmount = 0,
    pendingCount = 0,
    failedMissingChargeCount = 0,
} = {}) {
    const normalized = String(status || '').toLowerCase();
    const owed = Number(totalOwed) || 0;
    const completed = Number(completedAmount) || 0;
    const failed = Number(failedAmount) || 0;
    const pending = Number(pendingAmount) || 0;
    const notes = [];

    if (normalized === TIP_PAYOUT_STATUS_PENDING_PAID_TO_MANAGER) {
        notes.push(
            'Tips were paid to the manager\'s Stripe account. Mark as Paid (Manual) after the employee has been paid.',
        );
        return notes;
    }

    if (normalized === 'paid' && failed > 0.01) {
        notes.push(
            `Paid $${completed.toFixed(2)} covering this shift's tips & gratuity ($${owed.toFixed(2)}). `
            + `${failedCount} leftover transfer(s) totaling $${failed.toFixed(2)} failed`
            + (failedMissingChargeCount > 0
                ? ' (no Stripe charge to reclaim)'
                : '')
            + ' and do not change the paid amount.',
        );
        return notes;
    }

    if (normalized === 'partial') {
        notes.push(`Paid $${completed.toFixed(2)} of $${owed.toFixed(2)} owed for this shift.`);
        const remainingOwed = Math.max(0, owed - completed);
        const leftoverNote = describeLeftoverPendingTransfers({
            remainingOwed,
            pendingTransferAmount: pending,
            pendingCount,
        });
        if (leftoverNote) {
            notes.push(leftoverNote);
        }
        if (failed > 0.01) {
            notes.push(
                `${failedCount} transfer(s) totaling $${failed.toFixed(2)} failed`
                + (failedMissingChargeCount > 0
                    ? ' — some have no Stripe charge and cannot be reclaimed.'
                    : '.'),
            );
        }
        return notes;
    }

    if (normalized === 'failed' && failed > 0.01) {
        notes.push(
            failedMissingChargeCount > 0
                ? `${failedCount} transfer(s) totaling $${failed.toFixed(2)} failed because the original Stripe charge is missing. Retry will not succeed until a charge exists.`
                : `${failedCount} transfer(s) totaling $${failed.toFixed(2)} failed.`,
        );
    }

    return notes;
}
